import type { ListOutput } from '@allocab/allocab-standard-shared-types';
import { useQuery } from '@tanstack/react-query';

import type {
  DashboardRateInput,
  ExtranetIncidentOutput,
  IncidentCategory,
  IncidentCategoryBucket,
  PageableRangeInput,
} from '../../../types/extranet-api.types';
import { getExtranetApi } from '../extranetApi';
import { toPageableRangeInput } from '../mappers';
import { extranetQueryKeys } from '../queryKeys';

const SAMPLE_LIMIT = 500;

/**
 * Le back n'expose pas d'agrégation par catégorie : on compte côté front sur un
 * échantillon de la période. `totalItems` sert à signaler une troncature, pour
 * ne pas présenter une répartition partielle comme exhaustive. À remplacer si
 * Yohan livre une route d'agrégation dédiée.
 */
export interface IncidentCategoryBucketsResponse {
  buckets: IncidentCategoryBucket[];
  /** Nombre total d'incidents de la période, tous comptés ou non. */
  totalItems: number;
  /** true si la période contient plus d'incidents que l'échantillon compté. */
  truncated: boolean;
}

export const useIncidentCategoryBuckets = (input: DashboardRateInput) =>
  useQuery<IncidentCategoryBucketsResponse>({
    queryKey: extranetQueryKeys.dashboard.incidentCategoryBuckets(input),
    queryFn: async () => {
      const list = await getExtranetApi().get<PageableRangeInput, ListOutput<ExtranetIncidentOutput>>(
        '/v1/incidents',
        toPageableRangeInput(input, { offset: 0, limit: SAMPLE_LIMIT }),
      );

      const items = list.items ?? [];
      const counts = new Map<IncidentCategory, number>();
      for (const item of items) {
        counts.set(item.category, (counts.get(item.category) ?? 0) + 1);
      }

      // Seules les catégories présentes sont remontées : 14 cases dont 12 à zéro
      // ne dit rien d'utile.
      const buckets: IncidentCategoryBucket[] = Array.from(counts, ([category, count]) => ({ category, count })).sort(
        (a, b) => b.count - a.count,
      );

      const totalItems = list.totalItems ?? items.length;
      return { buckets, totalItems, truncated: totalItems > items.length };
    },
    enabled: Boolean(input.dateRange?.from && input.dateRange?.to),
  });
